// The bench controls for a device that is NOT a switch bank.
//
// Asked for 2026-10-25 (the owner), of a Curtain Controller connected in BLE
// Test: "there should be actually buttons as per device type … and you can
// fetch immediately data by mobile calling api for device details and there
// actually should come curtain buttons which are not in ble test. do this for
// all type of devices."
//
// ⚠️ THE SAME RULES AS THE CUSTOMER'S SCREEN, NOT A SECOND IMPLEMENTATION.
// A curtain here runs through `useCurtain` → `curtainRunner`, which owns the
// clock, the reverse and the repeat-is-a-stop rule. Writing a second set of
// curtain buttons for the bench is the one thing that must not happen: the motor
// is momentary and the APP owns the timing, so a bench copy that ran 3 s where
// the product runs 7 would "work" on the bench and be wrong in the house —
// exactly the difference this screen exists to rule out.
//
// ⚠️ HOW THE COMMANDS LEAVE IS THE SCREEN'S BUSINESS, NOT THIS FILE'S. Where a
// control has no shared hook (a lock's unlock word, a light preset) it calls
// `onCommand`, the bench's own logged path. Where it reuses a hook, the hook
// publishes through `publishCommand`, which routes over this very BLE link
// because BLE Test pins the device (`setForcedBle`) and adopts the session —
// and `bleControl.onBleTrace` puts those writes in the log too.
//
// ⚠️ A TYPE WITH NO BENCH CONTROLS SAYS SO. A hub, a scene controller, an RF
// receiver and the IR blaster have nothing to press here (the blaster's keypad
// is built from code lists on the server), and an empty panel reads as a broken
// screen. They get one honest line and the way to the full screen instead.

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import PauseIcon from '@mui/icons-material/Pause';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { deviceTypeKey, deviceLabel, unlockCommand } from '../../../core/devices/deviceMeta';
import useCurtain from '../../../core/hooks/useCurtain';
import { PresetButton } from '../../shared/controls/LightBits';
import { RGB_PRESETS, WWRGB_PRESETS } from '../../../core/devices/light';

// Which keys this file draws. Everything else stays with the switch panel
// (tiles / fan gauges / the WWH dimmer, which BleDevicePanel already had).
const DRAWN = ['curtain', 'door', 'rgb', 'wwrgb'];
// Types that genuinely have nothing to press on a bench, with the reason.
const NO_CONTROLS = {
  hub: 'A hub has no outputs — it relays for the lamps around it.',
  sceneController: 'Four scene buttons: the room’s scenes are the controls, and a bench device is in no room.',
  bleWwh: 'A BLE lamp is driven by advertising from the phone, not over this link — use the lamp tree.',
  irBlaster: 'An IR blaster’s keypad is built from the code list on the server; open the full screen for it.',
  singlePhase: 'A current sensor reports; there is nothing to switch.',
  threePhase: 'A current sensor reports; there is nothing to switch.',
  unsupported: '',
};

export const hasBenchPanel = (deviceId) => {
  const key = deviceTypeKey(deviceId);
  return DRAWN.includes(key) || Object.prototype.hasOwnProperty.call(NO_CONTROLS, key);
};

// ── Curtain / sliding gate (13, 51, 37) ───────────────────────────────────
function CurtainBench({ device, color, button, disabled }) {
  const {
    noun, labels, moving, opening, open, close, stop, remainingSeconds, times, loadingTimes,
  } = useCurtain(device);

  const Round = ({ onClick, children, label }) => (
    <Box sx={{ textAlign: 'center' }}>
      <Button
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 72,
          height: 72,
          minWidth: 0,
          borderRadius: '50%',
          bgcolor: button,
          color: '#fff',
          '&:hover': { bgcolor: button },
          '&.Mui-disabled': { bgcolor: `${button}66`, color: '#ffffff66' },
        }}
      >
        {children}
      </Button>
      <Typography sx={{ color: `${color}99`, fontSize: 11, mt: 0.5 }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5 }}>
        {/* The hook's own labels, so a sliding gate never says "Curtain". */}
        <Round onClick={open} label={labels.open}><KeyboardArrowLeftRoundedIcon sx={{ fontSize: 34 }} /></Round>
        <Round onClick={stop} label={labels.stop}><PauseIcon sx={{ fontSize: 28 }} /></Round>
        <Round onClick={close} label={labels.close}><KeyboardArrowRightRoundedIcon sx={{ fontSize: 34 }} /></Round>
      </Box>
      {/* ⚠️ The press is a TIMED RUN and the app owns the clock, so the bench
          shows the same countdown the customer sees — if the motor keeps going
          after it reaches zero, that is the finding. */}
      <Typography sx={{ color: `${color}99`, fontSize: 11, mt: 1.5 }}>
        {moving
          ? `${opening ? 'Opening' : 'Closing'} — ${Math.max(0, remainingSeconds)} s left`
          : `${noun} idle`}
      </Typography>
      <Typography sx={{ color: `${color}66`, fontSize: 10 }}>
        {loadingTimes
          ? 'reading the travel times…'
          : `travel: open ${times.openSeconds}s · close ${times.closeSeconds}s`
            + `${times.openSet || times.closeSet ? '' : ' (defaults — this board has no saved times)'}`}
      </Typography>
    </Box>
  );
}

// ── Door locks (3, 102) ───────────────────────────────────────────────────
// ⚠️ TWO LOCKS, TWO WORDS — 3 takes OPENDOOR and 102 LOCKUNLOCK, and a wrong
// word is not an error at either end: the door just stays shut. The word comes
// from deviceMeta.unlockCommand, never written out here.
// ⚠️ NO BIOMETRIC GATE ON THE BENCH, deliberately: this screen is Super Admin
// only and the lock is on a desk, not a door. The customer's path keeps its
// fingerprint check (core/auth/biometricGate).
function DoorBench({
  deviceId, color, button, disabled, onCommand,
}) {
  const word = unlockCommand(deviceId);
  return (
    <Box sx={{ py: 3, textAlign: 'center' }}>
      <Button
        onClick={() => onCommand && onCommand(word)}
        disabled={disabled}
        startIcon={<LockOpenIcon />}
        sx={{
          bgcolor: button, color: '#fff', borderRadius: '14px', px: 3, py: 1.25,
          textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: button },
        }}
      >
        Unlock
      </Button>
      <Typography sx={{ color: `${color}99`, fontSize: 11, mt: 1.5 }}>
        {`sends ${word} — the lock's own reply is the only proof it acted`}
      </Typography>
    </Box>
  );
}

// ── Colour lights (24 rgb, 23 wwrgb) ──────────────────────────────────────
// The presets are the product's own tables, so a bench tap sends exactly the
// bytes the customer's tap does. The sliders stay out: without the device's
// reported level there is nothing to put the handle at, and a slider that
// springs back from 0 reads as a broken control.
function LightBench({
  deviceId, color, disabled, onCommand,
}) {
  const presets = deviceTypeKey(deviceId) === 'rgb' ? RGB_PRESETS : WWRGB_PRESETS;
  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', px: 1,
      }}
      >
        {presets.map((p) => (
          <PresetButton
            key={p.label}
            label={p.label}
            swatch={p.swatch}
            onClick={() => { if (!disabled && onCommand) onCommand(p.command); }}
          />
        ))}
      </Box>
      <Typography sx={{ color: `${color}99`, fontSize: 11, mt: 1.5, textAlign: 'center' }}>
        The product’s own presets. A level slider needs the light to report where it is.
      </Typography>
    </Box>
  );
}

export default function BleTypePanel({
  deviceId, uniqueNumber, onCommand, disabled, color, button,
}) {
  const key = deviceTypeKey(deviceId);
  const device = { UniqueNumber: uniqueNumber, DeviceID: deviceId };

  if (key === 'curtain') {
    return <CurtainBench device={device} color={color} button={button} disabled={disabled} />;
  }
  if (key === 'door') {
    return (
      <DoorBench
        deviceId={deviceId}
        color={color}
        button={button}
        disabled={disabled}
        onCommand={onCommand}
      />
    );
  }
  if (key === 'rgb' || key === 'wwrgb') {
    return <LightBench deviceId={deviceId} color={color} disabled={disabled} onCommand={onCommand} />;
  }

  const why = NO_CONTROLS[key];
  return (
    <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
      <Typography sx={{ color: `${color}99`, fontSize: 12 }}>
        {`${deviceLabel(deviceId)} — no bench controls.`}
      </Typography>
      {!!why && (
        <Typography sx={{ color: `${color}66`, fontSize: 11, mt: 0.5 }}>{why}</Typography>
      )}
      <Typography sx={{ color: `${color}66`, fontSize: 11, mt: 0.5 }}>
        The raw probe buttons are under ⋮ Diagnostics.
      </Typography>
    </Box>
  );
}
